/*
 * Snyvia: storefront ecommerce events that Shopify's standard events don't cover.
 *
 * Publishes custom events with Shopify.analytics.publish(). The "Snyvia GA4 extra events"
 * custom pixel (Settings > Customer events) forwards them to GA4. Standard events
 * (page_view, view_item, add_to_cart, begin_checkout, purchase, ...) are already sent by the
 * Google & YouTube app and are deliberately NOT sent here, to avoid double counting.
 *
 * Events: view_item_list, select_item, view_promotion, select_promotion, select_content.
 * Debug: add ?snyvia_debug=1 to any URL to log events in the browser console.
 */
(() => {
  const script = document.currentScript;
  const TEMPLATE = script?.dataset.template || '';
  const COLLECTION_TITLE = script?.dataset.collectionTitle || '';
  const CURRENCY = window.Shopify?.currency?.active || 'INR';
  const DEBUG = /[?&]snyvia_debug=1/.test(location.search);
  const MAX_LIST_ITEMS = 50;

  const publish = (name, data) => {
    const payload = { currency: CURRENCY, ...data };
    try {
      window.Shopify?.analytics?.publish?.(name, payload);
    } catch (e) {
      /* never break the storefront */
    }
    if (DEBUG) console.log('[snyvia-analytics]', name, payload);
  };

  const text = (el) => (el?.textContent || '').replace(/\s+/g, ' ').trim();
  const sectionOf = (el) => el.closest('.shopify-section');
  // "shopify-section-template--123__product_list_Ttb8mq" -> "product_list_Ttb8mq"
  const sectionKey = (section) => (section?.id || '').split('__').pop() || 'unknown';
  const sectionType = (section) => sectionKey(section).replace(/_[A-Za-z0-9]{6}$/, '');

  const priceFromText = (s) => {
    const m = (s || '').replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
    return m ? Number(m[1]) : undefined;
  };

  // Item data: from the data-analytics-item attribute (set in product-card.liquid), with a DOM fallback.
  const itemFromCard = (card) => {
    let item = {};
    try {
      item = JSON.parse(card.dataset.analyticsItem || '{}');
    } catch (e) {}
    const link = card.querySelector('a[href*="/products/"]');
    if (!item.product_id) item.product_id = card.dataset.productId;
    if (!item.variant_id && link) item.variant_id = new URL(link.href, location.origin).searchParams.get('variant') || undefined;
    if (!item.item_name) item.item_name = text(card.querySelector('.product-card__link .visually-hidden')) || text(card.querySelector('a[href*="/products/"]'));
    if (item.price === undefined) item.price = priceFromText(text(card.querySelector('product-price, .price')));
    return item;
  };

  const listOf = (section) => {
    const type = sectionType(section);
    if (TEMPLATE.startsWith('collection') && /main/.test(type)) {
      return { item_list_id: `collection_${location.pathname.split('/')[2] || ''}`, item_list_name: `Collection: ${COLLECTION_TITLE}` };
    }
    if (TEMPLATE.startsWith('search') && /main/.test(type)) {
      return { item_list_id: 'search_results', item_list_name: 'Search results' };
    }
    const heading = text(section?.querySelector('h1, h2, h3, h4, h5, h6'));
    return {
      item_list_id: `${TEMPLATE || 'page'}_${sectionKey(section)}`,
      item_list_name: `${TEMPLATE || 'page'}: ${heading || type.replace(/_/g, ' ')}`,
    };
  };

  const cardsIn = (section) => [...section.querySelectorAll('product-card[data-product-id]:not([data-placeholder])')];

  const itemsFor = (section, cards) => {
    const list = listOf(section);
    return cards.map((card) => ({ ...itemFromCard(card), ...list, index: cardsIn(section).indexOf(card) }));
  };

  // ---- view_item_list: once per list, when at least 30% of it is on screen.
  // Collection pages are skipped: the Google app already sends view_item_list there.
  const seenLists = new WeakSet();
  const listObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting || seenLists.has(entry.target)) continue;
      seenLists.add(entry.target);
      listObserver.unobserve(entry.target);
      const cards = cardsIn(entry.target).slice(0, MAX_LIST_ITEMS);
      if (!cards.length) continue;
      publish('view_item_list', { ...listOf(entry.target), items: itemsFor(entry.target, cards) });
    }
  }, { threshold: 0.3 });

  // ---- view_promotion: hero slides, once per slide, when 60% visible.
  const promoOf = (slide) => {
    const section = sectionOf(slide);
    const slides = [...section.querySelectorAll('.chs__slide')];
    const i = slides.indexOf(slide);
    const img = slide.querySelector('img');
    const creative = img ? (img.currentSrc || img.src).split('/').pop().split('?')[0] : '';
    return {
      promotion_id: `${sectionKey(section)}_${i + 1}`,
      promotion_name: text(slide.querySelector('h1, h2, h3, .chs__heading')) || img?.alt || creative || `Hero slide ${i + 1}`,
      creative_name: creative,
      creative_slot: `hero_slide_${i + 1}`,
    };
  };
  const seenPromos = new WeakSet();
  const promoObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting || seenPromos.has(entry.target)) continue;
      seenPromos.add(entry.target);
      promoObserver.unobserve(entry.target);
      publish('view_promotion', { ...promoOf(entry.target) });
    }
  }, { threshold: 0.6 });

  const scan = () => {
    for (const card of document.querySelectorAll('product-card[data-product-id]:not([data-placeholder])')) {
      const section = sectionOf(card);
      if (!section || seenLists.has(section) || section.dataset.snyviaListObserved) continue;
      if (TEMPLATE.startsWith('collection') && /main/.test(sectionType(section))) continue;
      section.dataset.snyviaListObserved = '1';
      listObserver.observe(section);
    }
    for (const slide of document.querySelectorAll('.chs__slide')) {
      if (slide.dataset.snyviaPromoObserved) continue;
      slide.dataset.snyviaPromoObserved = '1';
      promoObserver.observe(slide);
    }
  };

  // ---- Clicks: select_item, select_promotion, select_content.
  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;

    // Product card -> product page (ignore quick-add buttons, swatches, forms).
    const card = target.closest('product-card[data-product-id]');
    if (card) {
      if (target.closest('button, form, input, select, label, quick-add-component')) return;
      const section = sectionOf(card);
      if (!section) return;
      publish('select_item', { ...listOf(section), items: itemsFor(section, [card]) });
      return;
    }

    const link = target.closest('a[href]');
    if (!link) return;

    // Hero banner buttons.
    const slide = link.closest('.chs__slide');
    if (slide) {
      publish('select_promotion', { ...promoOf(slide), link_url: link.href, link_text: text(link) });
      return;
    }

    // Category circles and collection tiles.
    const section = sectionOf(link);
    const type = sectionType(section);
    if (/circle_menu|collection_list|collection_links/.test(type) && /\/collections\//.test(link.href)) {
      publish('select_content', {
        content_type: type.startsWith('circle_menu') ? 'category_circle' : 'collection_tile',
        content_id: new URL(link.href, location.origin).pathname,
        link_text: text(link.closest('.swiper-slide, li, .collection-card, div')) || text(link),
        list_name: `${TEMPLATE || 'page'}: ${text(section?.querySelector('h1, h2, h3, h4')) || type.replace(/_/g, ' ')}`,
      });
    }
  }, true);

  // Lists and slides can be added later (recommendations, infinite scroll, section reloads in the editor).
  let timer;
  new MutationObserver(() => {
    clearTimeout(timer);
    timer = setTimeout(scan, 400);
  }).observe(document.body, { childList: true, subtree: true });
  scan();
})();
